import { useStore } from '@tanstack/react-form';
import { useFormContext } from './FormProvider';

/**
 * Decides *when* a field's error becomes visible (§2 of GRAIN-2551): after the
 * field has been typed in and blurred, or once the form has been submitted.
 * Errors are computed live by the schema; this only gates their display.
 */
export function shouldShowFieldError(
  meta: { errors?: unknown[]; isBlurred?: boolean; isDirty?: boolean },
  submitted: boolean,
): boolean {
  if (!meta.errors?.length) return false;
  return isFieldErrorTimingOpen(meta, submitted);
}

/**
 * The timing gate on its own, for fields whose invalid state is a boolean
 * (a red trigger with no copy) rather than a message.
 */
export function isFieldErrorTimingOpen(
  meta: { isBlurred?: boolean; isDirty?: boolean },
  submitted: boolean,
): boolean {
  return submitted || (Boolean(meta.isBlurred) && Boolean(meta.isDirty));
}

/**
 * The `submitted` argument for {@link shouldShowFieldError}, for any field
 * inside a shared `<Form>`. Uses `submissionAttempts` rather than `isSubmitted`
 * so a blocked (invalid) submit still reveals every field's error.
 */
export function useFieldErrorVisibility(): boolean {
  const { form } = useFormContext();
  return useStore(form.store, (state) => state.submissionAttempts > 0);
}

/**
 * For fields whose real-world validity can't be expressed in the zod schema
 * (business rules needing external data, format checks that would need
 * `.refine` — see contact.schema.ts) — a schema error still wins when
 * present; `fallback` only runs once the same §2 timing gate opens.
 */
export function computeGatedFieldMessage(
  meta: { errors?: unknown[]; isBlurred?: boolean; isDirty?: boolean },
  submitted: boolean,
  fallback: () => string | undefined,
): string | undefined {
  if (!isFieldErrorTimingOpen(meta, submitted)) return undefined;

  const schemaMessage = meta.errors?.length
    ? (meta.errors[0] as { message?: string } | undefined)?.message
    : undefined;
  return schemaMessage ?? fallback();
}
